import React, { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Link2, Power, GripVertical } from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red";

function ItemRow({ it, i, updItem, delItem }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={it}
      dragListener={false}
      dragControls={controls}
      className="flex gap-2 items-center mb-2 bg-white"
      data-testid={`og-item-${i}`}
    >
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
        title="Prevuci za promenu redosleda"
        data-testid={`og-item-drag-${i}`}
      >
        <GripVertical size={16} />
      </button>
      <input className={inputCls} value={it.name} onChange={(e) => updItem(i, "name", e.target.value)} placeholder="npr. Garlic sos" />
      <input type="number" className="w-24 px-2 py-2 rounded-lg border-2 border-slate-200" value={it.additional_price} onChange={(e) => updItem(i, "additional_price", Number(e.target.value))} />
      <button type="button" title="Dostupnost" onClick={() => updItem(i, "active", !it.active)} className={it.active ? "text-brand-green" : "text-slate-300"}><Power size={16} /></button>
      <button type="button" onClick={() => delItem(i)} className="text-slate-400"><X size={15} /></button>
    </Reorder.Item>
  );
}

function GroupEditor({ group, rid, onClose, onSaved }) {
  const [f, setF] = useState(() => group || { name: "", selection_type: "single", required: false, min_selection: 0, max_selection: 1, active: true, kitchen_color: "DEFAULT", items: [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addItem = () => set("items", [...f.items, { id: crypto.randomUUID(), name: "", additional_price: 0, active: true }]);
  const updItem = (i, k, v) => { const a = [...f.items]; a[i] = { ...a[i], [k]: v }; set("items", a); };
  const delItem = (i) => set("items", f.items.filter((_, x) => x !== i));
  const save = async () => {
    if (!f.name) { toast.error("Naziv je obavezan"); return; }
    try {
      if (group?.id) await api.put(`/admin/option-groups/${group.id}`, { ...f });
      else await api.post(`/admin/option-groups`, { ...f, restaurant_id: rid });
      toast.success("Sačuvano"); onSaved();
    } catch { toast.error("Greška pri čuvanju"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-black text-xl">{group?.id ? "Izmeni grupu opcija" : "Nova grupa opcija"}</h3><button onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={16} /></button></div>
        <label className="block mb-3"><span className="text-xs font-semibold text-slate-500 uppercase">Naziv grupe</span><input data-testid="og-name" className={inputCls + " mt-1"} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="npr. Odaberi sos" /></label>
        <div className="flex flex-wrap gap-4 items-center mb-3">
          <select className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm" value={f.selection_type} onChange={(e) => set("selection_type", e.target.value)}><option value="single">Jedan izbor</option><option value="multi">Više izbora</option></select>
          <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={f.required} onChange={(e) => set("required", e.target.checked)} />Obavezno</label>
          <label className="text-sm flex items-center gap-1">Min<input type="number" className="w-16 px-2 py-1 rounded border-2 border-slate-200 ml-1" value={f.min_selection} onChange={(e) => set("min_selection", Number(e.target.value))} /></label>
          <label className="text-sm flex items-center gap-1">Max<input type="number" className="w-16 px-2 py-1 rounded border-2 border-slate-200 ml-1" value={f.max_selection} onChange={(e) => set("max_selection", Number(e.target.value))} /></label>
        </div>
        <label className="block mb-3"><span className="text-xs font-semibold text-slate-500 uppercase">Boja u porudžbinama</span>
          <select data-testid="og-color" className={inputCls + " mt-1"} value={f.kitchen_color || "DEFAULT"} onChange={(e) => set("kitchen_color", e.target.value)}>
            <option value="DEFAULT">Podrazumevana</option>
            <option value="GREEN">Zelena</option>
            <option value="YELLOW">Žuta</option>
            <option value="RED">Crvena</option>
            <option value="BLUE">Plava</option>
            <option value="ORANGE">Narandžasta</option>
            <option value="PURPLE">Ljubičasta</option>
            <option value="CYAN">Tirkizna</option>
          </select>
        </label>
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2"><h4 className="font-bold text-sm">Opcije</h4><button data-testid="og-add-item" onClick={addItem} className="text-brand-red text-sm font-semibold flex items-center gap-1"><Plus size={14} />Opcija</button></div>
          <Reorder.Group axis="y" values={f.items} onReorder={(v) => set("items", v)}>
            {f.items.map((it, i) => (
              <ItemRow key={it.id} it={it} i={i} updItem={updItem} delItem={delItem} />
            ))}
          </Reorder.Group>
          {f.items.length > 1 && <p className="text-[11px] text-slate-400">Prevuci ⠿ da promeniš redosled opcija.</p>}
        </div>
        <button data-testid="og-save" onClick={save} className="w-full bg-brand-red text-white rounded-lg py-3 font-bold mt-4">Sačuvaj</button>
      </div>
    </div>
  );
}

function LinkModal({ group, rid, onClose }) {
  const [prods, setProds] = useState([]);
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(new Set());
  useEffect(() => { (async () => {
    const m = await api.get(`/admin/menu?restaurant_id=${rid}`); setProds(m.data.products); setCats(m.data.categories);
    const g = await api.get(`/admin/option-groups/${group.id}/products`); setSel(new Set(g.data.product_ids));
  })(); }, []);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectCat = (cid) => setSel((s) => { const n = new Set(s); prods.filter((p) => p.category_id === cid).forEach((p) => n.add(p.id)); return n; });
  const save = async () => { try { await api.put(`/admin/option-groups/${group.id}/products`, { product_ids: [...sel] }); toast.success("Povezano sa proizvodima"); onClose(); } catch { toast.error("Greška"); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="link-products-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-black text-lg">Poveži: {group.name}</h3><button onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={16} /></button></div>
        {cats.map((c) => (
          <div key={c.id} className="mb-3">
            <div className="flex items-center justify-between"><p className="font-bold text-sm">{c.name}</p><button onClick={() => selectCat(c.id)} className="text-xs text-brand-red font-semibold">Sve u kategoriji</button></div>
            {prods.filter((p) => p.category_id === c.id).map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1 text-sm"><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} />{p.name}</label>
            ))}
          </div>
        ))}
        <button data-testid="link-save" onClick={save} className="w-full bg-brand-red text-white rounded-lg py-3 font-bold mt-2">Sačuvaj</button>
      </div>
    </div>
  );
}

function GroupCard({ g, onLink, onEdit, onDup, onDel, dragEnabled, persist }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={g}
      dragListener={false}
      dragControls={controls}
      onDragEnd={persist}
      className="bg-white rounded-xl border p-4"
      data-testid={`option-group-${g.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {dragEnabled && (
            <button
              type="button"
              onPointerDown={(e) => controls.start(e)}
              className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none shrink-0"
              title="Prevuci za promenu redosleda"
              data-testid={`group-drag-${g.id}`}
            >
              <GripVertical size={18} />
            </button>
          )}
          <p className="font-display font-black truncate">{g.name}</p>
        </div>
        <span className="text-[10px] font-bold uppercase bg-slate-100 rounded-full px-2 py-0.5 shrink-0">{g.selection_type === "multi" ? "Više" : "Jedan"}{g.required ? " · obavezno" : ""}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">{(g.items || []).length} opcija · povezano sa {g.linked_count} proizvoda</p>
      <div className="text-xs text-slate-500 mt-1 line-clamp-1">{(g.items || []).map((i) => i.name).join(", ")}</div>
      <div className="flex gap-3 mt-3">
        <button data-testid={`link-${g.id}`} onClick={() => onLink(g)} className="text-sm font-semibold text-brand-red flex items-center gap-1"><Link2 size={14} />Poveži proizvode</button>
        <button onClick={() => onEdit(g)} className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Pencil size={14} />Izmeni</button>
        <button onClick={() => onDup(g)} className="text-sm font-semibold text-slate-500">Dupliraj</button>
        <button onClick={() => onDel(g)} className="text-slate-400 hover:text-brand-red ml-auto"><Trash2 size={16} /></button>
      </div>
    </Reorder.Item>
  );
}

export function OptionsManager({ restaurants }) {
  const [rid, setRid] = useState(restaurants[0]?.id || "");
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(null);
  const [q, setQ] = useState("");
  const load = async () => { if (!rid) return; const { data } = await api.get(`/admin/option-groups?restaurant_id=${rid}`); setGroups(data); };
  useEffect(() => { load(); }, [rid]);
  useEffect(() => { if (!rid && restaurants[0]) setRid(restaurants[0].id); }, [restaurants]);
  const del = async (g) => {
    try { await api.delete(`/admin/option-groups/${g.id}`); toast.success("Obrisano"); load(); }
    catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(e.response.data.detail + " Svejedno obrisati?")) { await api.delete(`/admin/option-groups/${g.id}?force=true`); load(); }
      } else toast.error("Greška");
    }
  };
  const dup = async (g) => { await api.post(`/admin/option-groups/${g.id}/duplicate`, {}); load(); };
  const persistOrder = async () => {
    try { await api.post(`/admin/option-groups/reorder`, { restaurant_id: rid, ordered_ids: groups.map((g) => g.id) }); }
    catch { toast.error("Greška pri čuvanju redosleda"); }
  };
  const dragEnabled = !q;
  const shown = groups.filter((g) => !q || g.name.toLowerCase().includes(q.toLowerCase()) || (g.items || []).some((i) => i.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-5">
        <select data-testid="opt-rest-select" className={inputCls + " max-w-xs"} value={rid} onChange={(e) => setRid(e.target.value)}>{restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <input data-testid="opt-search" className={inputCls + " max-w-xs"} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraži opcije" />
        <button data-testid="new-option-group" onClick={() => setEditing({})} className="bg-brand-red text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1 ml-auto"><Plus size={16} />Nova grupa opcija</button>
      </div>
      {dragEnabled && groups.length > 1 && <p className="text-[11px] text-slate-400 mb-2">Prevuci ⠿ da promeniš redosled grupa. Redosled se odražava u meniju.</p>}
      {dragEnabled ? (
        <Reorder.Group axis="y" values={groups} onReorder={setGroups} className="grid gap-3 max-w-2xl">
          {groups.map((g) => (
            <GroupCard key={g.id} g={g} onLink={setLinking} onEdit={setEditing} onDup={dup} onDel={del} dragEnabled persist={persistOrder} />
          ))}
          {groups.length === 0 && <p className="text-sm text-slate-300">Još nema grupa opcija.</p>}
        </Reorder.Group>
      ) : (
        <div className="grid gap-3 max-w-2xl">
          {shown.map((g) => (
            <div key={g.id} data-testid={`option-group-${g.id}`} className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between">
                <p className="font-display font-black truncate">{g.name}</p>
                <span className="text-[10px] font-bold uppercase bg-slate-100 rounded-full px-2 py-0.5 shrink-0">{g.selection_type === "multi" ? "Više" : "Jedan"}{g.required ? " · obavezno" : ""}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{(g.items || []).length} opcija · povezano sa {g.linked_count} proizvoda</p>
              <div className="text-xs text-slate-500 mt-1 line-clamp-1">{(g.items || []).map((i) => i.name).join(", ")}</div>
              <div className="flex gap-3 mt-3">
                <button data-testid={`link-${g.id}`} onClick={() => setLinking(g)} className="text-sm font-semibold text-brand-red flex items-center gap-1"><Link2 size={14} />Poveži proizvode</button>
                <button onClick={() => setEditing(g)} className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Pencil size={14} />Izmeni</button>
                <button onClick={() => dup(g)} className="text-sm font-semibold text-slate-500">Dupliraj</button>
                <button onClick={() => del(g)} className="text-slate-400 hover:text-brand-red ml-auto"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {shown.length === 0 && <p className="text-sm text-slate-300">Nema rezultata.</p>}
        </div>
      )}
      {editing !== null && <GroupEditor group={editing.id ? editing : null} rid={rid} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {linking && <LinkModal group={linking} rid={rid} onClose={() => { setLinking(null); load(); }} />}
    </div>
  );
}
